import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { Eye, EyeOff, Loader2, MessageCircle } from 'lucide-react'
import { formatPhone, addDDI } from '../../utils/masks'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [aguardandoCodigo, setAguardandoCodigo] = useState(false)
  const [precisaTelefone, setPrecisaTelefone] = useState(false)
  const [telefone, setTelefone] = useState('')
  const [codigo, setCodigo] = useState('')
  const [lembrarDispositivo, setLembrarDispositivo] = useState(true)

  const { login, submitPhoneFor2FA, verifyTwoFactor, isLoading, canais2FA } = useAuthStore()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const result = await login(email, password)
    if (!result.success) {
      setError(result.error)
    } else if (result.requiresPhone) {
      setPrecisaTelefone(true)
    } else if (result.requires2fa) {
      setAguardandoCodigo(true)
    }
  }

  const handleSubmitTelefone = async (e) => {
    e.preventDefault()
    setError('')

    const result = await submitPhoneFor2FA(addDDI(telefone))
    if (!result.success) {
      setError(result.error)
    } else {
      setPrecisaTelefone(false)
      setAguardandoCodigo(true)
    }
  }

  const handleVerifyCode = async (e) => {
    e.preventDefault()
    setError('')

    const result = await verifyTwoFactor(codigo, lembrarDispositivo)
    if (!result.success) {
      setError(result.error)
    }
  }

  const handleWhatsApp = () => {
    window.open('https://wa.me/5534999266484?text=Ol%C3%A1%2C%20gostaria%20de%20contratar%20o%20sistema%20Rematei', '_blank')
  }

  const currentYear = new Date().getFullYear()

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(30,144,255,0.1),transparent_50%)] pointer-events-none"></div>
      
      <div className="relative w-full max-w-md">
        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
          {/* Logo Section */}
          <div className="text-center mb-8">
            <div className="mb-6">
              <img 
                src="/assets/logo_rematei_new.png" 
                alt="Rematei" 
                className="h-16 mx-auto object-contain drop-shadow-2xl"
              />
            </div>
            
            <p className="text-gray-600 text-sm font-medium">
              Sistema de Gestão de Leilões
            </p>
          </div>

          {/* Form */}
          {precisaTelefone ? (
          <form onSubmit={handleSubmitTelefone} className="space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="text-center">
              <p className="text-gray-600 text-sm">
                Não conseguimos confirmar seu e-mail. Informe seu WhatsApp para receber o código de verificação.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                WhatsApp
              </label>
              <input
                type="text"
                required
                autoFocus
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 outline-none transition-all duration-200 bg-gray-50 focus:bg-white text-gray-900"
                placeholder="(00) 00000-0000"
                value={telefone}
                onChange={(e) => setTelefone(formatPhone(e.target.value))}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 size={20} className="mr-2 animate-spin" />
                  Enviando...
                </div>
              ) : (
                'Enviar código pelo WhatsApp'
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => { setPrecisaTelefone(false); setTelefone(''); setError('') }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Voltar
              </button>
            </div>
          </form>
          ) : !aguardandoCodigo ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 outline-none transition-all duration-200 bg-gray-50 focus:bg-white text-gray-900"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Senha
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 outline-none transition-all duration-200 bg-gray-50 focus:bg-white text-gray-900"
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 size={20} className="mr-2 animate-spin" />
                  Entrando...
                </div>
              ) : (
                'Entrar no Sistema'
              )}
            </button>

            <div className="text-center">
              <Link to="/esqueci-senha" className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors">
                Esqueceu sua senha?
              </Link>
            </div>
          </form>
          ) : (
          <form onSubmit={handleVerifyCode} className="space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="text-center">
              <p className="text-gray-600 text-sm">
                Enviamos um código de verificação para o seu {canais2FA.includes('whatsapp') ? 'e-mail e WhatsApp' : 'e-mail'}. Digite-o abaixo para confirmar o login neste dispositivo.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Código de verificação
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                autoFocus
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 outline-none transition-all duration-200 bg-gray-50 focus:bg-white text-gray-900 text-center text-2xl tracking-[0.5em] font-bold"
                placeholder="000000"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={lembrarDispositivo}
                onChange={(e) => setLembrarDispositivo(e.target.checked)}
                className="rounded border-gray-300"
              />
              Confiar neste dispositivo por 30 dias
            </label>

            <button
              type="submit"
              disabled={isLoading || codigo.length !== 6}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 size={20} className="mr-2 animate-spin" />
                  Verificando...
                </div>
              ) : (
                'Confirmar código'
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => { setAguardandoCodigo(false); setCodigo(''); setError('') }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Voltar
              </button>
            </div>
          </form>
          )}

          {/* WhatsApp Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleWhatsApp}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 transform hover:-translate-y-0.5"
            >
              <MessageCircle size={20} />
              <span>Contratar Agora</span>
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              © {currentYear} Rematei - Todos os direitos reservados
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}